"use client";

import React from "react";
import { Phone, Mail, MapPin, Loader, Send } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
export default function Contact() {
  return <section id="contact" className="w-full bg-background">
      <div className="flex flex-col lg:flex-row min-h-[600px]">
        {}
        <div className="w-full lg:w-[40%] bg-sidebar text-sidebar-foreground p-10 lg:p-16 flex flex-col justify-center relative overflow-hidden">
          {}
          <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none">
            <div className="absolute right-0 top-0 w-64 h-64 bg-primary rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="absolute left-0 bottom-0 w-64 h-64 bg-primary rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
          </div>

          <div className="relative z-10 space-y-12">
            <div>
              <h2 className="text-3xl font-bold tracking-tight mb-4 text-white">
                Kontaktieren Sie uns
              </h2>
              <p className="text-sidebar-foreground/80 text-lg leading-relaxed">
                Wir freuen uns auf Ihre Anfrage. Lassen Sie uns gemeinsam Ihr
                nächstes Bauprojekt realisieren.
              </p>
            </div>

            <div className="space-y-8">
              <div className="flex items-start gap-4 group">
                <div className="p-3 rounded-lg border border-sidebar-foreground/10 bg-sidebar-foreground/5 group-hover:border-primary/50 transition-colors">
                  <Phone className="size-5 text-primary" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-medium text-white mb-1">Telefon</h3>
                  <Link to="tel:+492211234567" className="text-sidebar-foreground/80 hover:text-primary transition-colors block">+49 173 48 70 574<br />+49 179 96 48 177</Link>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="p-3 rounded-lg border border-sidebar-foreground/10 bg-sidebar-foreground/5 group-hover:border-primary/50 transition-colors">
                  <Mail className="size-5 text-primary" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-medium text-white mb-1">E-Mail</h3>
                  <Link to="mailto:info@lecibau.de" className="text-sidebar-foreground/80 hover:text-primary transition-colors block">
                    info@lecibau.de
                  </Link>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="p-3 rounded-lg border border-sidebar-foreground/10 bg-sidebar-foreground/5 group-hover:border-primary/50 transition-colors">
                  <MapPin className="size-5 text-primary" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-medium text-white mb-1">Region</h3>
                  <p className="text-sidebar-foreground/80">Hamburg und Umgebung</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="w-full lg:w-[60%] p-8 lg:p-16 flex flex-col justify-center bg-background">
          <div className="max-w-xl w-full mx-auto">
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-foreground mb-2">
                Anfrage senden
              </h3>
              <p className="text-muted-foreground">
                Füllen Sie das Formular aus und wir melden uns zeitnah bei Ihnen.
              </p>
            </div>

            <FormProvider formId="contact_form_home" sectionName="contact" defaultValues={{
            name: "",
            email: "",
            phone: "",
            subject: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              pattern: "phone"
            },
            message: {
              required: true
            }
          }} submitText="Nachricht senden" successMessage="Vielen Dank! Ihre Nachricht wurde erfolgreich gesendet." errorMessage="Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.">
              <ContactFormContent />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-green-50/50 border border-green-200 rounded-lg text-center animate-in fade-in zoom-in duration-300">
        <div className="w-12 h-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <Send className="size-6" />
        </div>
        <h3 className="text-xl font-semibold text-green-900 mb-2">
          Nachricht gesendet!
        </h3>
        <p className="text-green-800 mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-green-200 text-green-700 hover:bg-green-100 hover:text-green-800">
          Neue Nachricht schreiben
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Bitte geben Sie Ihren Namen ein."
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Ihr Name</FormLabel>
              <FormControl>
                <Input placeholder="Max Mustermann" {...field} className="bg-background border-input focus-visible:ring-primary" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "E-Mail Adresse ist erforderlich.",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Bitte geben Sie eine gültige E-Mail Adresse ein."
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>E-Mail Adresse</FormLabel>
              <FormControl>
                <Input type="email" placeholder="max@example.com" {...field} className="bg-background border-input focus-visible:ring-primary" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel>Telefonnummer (Optional)</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+49 221 1234567" {...field} className="bg-background border-input focus-visible:ring-primary" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
              <FormLabel>Betreff (Optional)</FormLabel>
              <FormControl>
                <Input placeholder="Anfrage zu Rohbauarbeiten" {...field} className="bg-background border-input focus-visible:ring-primary" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Bitte geben Sie eine Nachricht ein."
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Nachricht</FormLabel>
            <FormControl>
              <Textarea placeholder="Erzählen Sie uns von Ihrem Projekt..." className="min-h-[150px] bg-background border-input focus-visible:ring-primary resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-medium bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Wird gesendet...
          </> : "Absenden"}
      </Button>
    </Form>;
}